// src/components/Apm/Quote/Quotes.js

import { useCallback, useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';

import { Alert, useMediaQuery, useTheme } from '@mui/material';

import TableBasic from '../Tables/TableBasic';
import { columnsQuotes } from '../Tables/Columns/columnsQuotes';
import { useDeleteQuoteMutation, useGetQuotesQuery } from '../../../redux/features/quoteApi';
import useDocumentListQuery from '../../../hooks/use-document-list-query';

const MOBILE_COLUMN_IDS = new Set(['qn', 'date_inv', 'amount', 'quote_to', 'actions']);

const QUOTE_ORDERING_FIELDS = {
  qn: 'qn',
  date_inv: 'date_inv',
  quote_to: 'bill_to__companyname',
  load_address: 'load_address',
  unload_address: 'unload_address',
  status: 'status__order_number',
};

const serializeQuoteSearch = (values) => ({
  bill_to: values?.bill_to?.uf ?? values?.bill_to?.id ?? undefined,
  load_detail: values?.load_detail || undefined,
  load_address: values?.load_address || undefined,
  unload_address: values?.unload_address || undefined,
  country_load: values?.loading_country?.value ?? undefined,
  country_unload: values?.unloading_country?.value ?? undefined,
  date_min: values?.date_min || undefined,
  date_max: values?.date_max || undefined,
  invoice_from: values?.invoice_from || undefined,
  invoice_to: values?.invoice_to || undefined,
  iteminv_invs: values?.iteminv_invs?.id ?? undefined,
  currency: values?.currency?.id ?? undefined,
  comment_invs: values?.comment_invs || undefined,
  status: values?.status?.serial_number ?? undefined,
});

const Quotes = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const smMediaDown = useMediaQuery(theme.breakpoints.down('sm'));
  const { searchQuote = {} } = useSelector((state) => state.searchQuoteSlice ?? {});

  const filters = useMemo(() => serializeQuoteSearch(searchQuote), [searchQuote]);
  const tableState = useDocumentListQuery(filters, QUOTE_ORDERING_FIELDS);

  const queryParams = useMemo(
    () => ({
      ...filters,
      ...tableState.paginationParams,
    }),
    [filters, tableState.paginationParams]
  );

  const {
    data: quotePage,
    isLoading: isQuotesLoading,
    isFetching: isQuotesFetching,
    isError,
    error,
    refetch: refetchQuotes,
  } = useGetQuotesQuery(queryParams);

  const [deleteQuote, { isLoading: isDeletingQuote }] = useDeleteQuoteMutation();
  const quotes = quotePage?.results ?? [];
  const totalCount = quotePage?.count ?? 0;

  useEffect(() => {
    tableState.ensureValidPage(totalCount);
  }, [tableState.ensureValidPage, totalCount, tableState]);

  const columns = useMemo(() => {
    const allColumns = columnsQuotes().map((column) => ({
      ...column,
      disableSorting: column.disableSorting || !QUOTE_ORDERING_FIELDS[column.id],
    }));

    if (!smMediaDown) {
      return allColumns;
    }

    return allColumns.filter((column) => MOBILE_COLUMN_IDS.has(column.id));
  }, [smMediaDown]);

  const handleDelete = useCallback(
    async (quoteId) => {
      await deleteQuote(quoteId).unwrap();
    },
    [deleteQuote]
  );

  if (isError) {
    return <Alert severity="error">{error?.data?.detail || t('toast.quote_list_not_updated')}</Alert>;
  }

  return (
    <TableBasic
      columns={columns}
      items={quotes}
      totalCount={totalCount}
      resourceType="quote"
      isFetching={isQuotesLoading || isQuotesFetching}
      isDeleting={isDeletingQuote}
      refetchItems={refetchQuotes}
      onDeleteItem={handleDelete}
      page={tableState.page}
      rowsPerPage={tableState.rowsPerPage}
      order={tableState.order}
      orderBy={tableState.orderBy}
      quickSearchInput={tableState.quickSearchInput}
      onQuickSearchChange={tableState.setQuickSearchInput}
      onPageChange={tableState.handlePageChange}
      onRowsPerPageChange={tableState.handleRowsPerPageChange}
      onSortChange={tableState.handleSortChange}
    />
  );
};

export default Quotes;
